import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Flex,
  Group,
  Modal,
  Pagination,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
  UnstyledButton,
  useMantineTheme,
} from '@mantine/core';
import { IconArrowLeft, IconChevronRight, IconChevronUp, IconChevronDown, IconMail, IconUsers } from '@tabler/icons-react';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import { classifyTouchpoint, attributionLabelOf, TouchpointRole } from './assists';
import AggregatedReportsAPI from '../../api/AggregatedReportsApi';
import CampaignReport from '../../api/CampaignReport';
import { VisiLoader } from '../GeneralComponent/GeneralComponent';

export type AggregateCustomerJourneyParams = {
  report_id: string;
  visiopt_campaign_id: string[];
  filter_visiopt_campaign_ids?: string[];
  attribution_model: string;
  attribute_window_type?: string;
  lookback_days?: number;
  report_time: string;
  goal_key: string;
  journey_type?: 'ad_network' | 'goal' | 'source' | 'tracking_tag' | 'custom_tag';
  /* Which of the row's numbers was clicked. 'assisted' lists the conversions behind the
     Assisted figure - the ones this ad was in the journey for but did not win. Absent means
     the long-standing credited-conversions drill-down. */
  journey_metric?: 'assisted';
  traffic_source?: string | string[];
  report_traffic_source?: string[];
  level?: 'campaign' | 'adset' | 'ad';
  ad_entity_id?: string;
  parent_campaign_id?: string;
  parent_adset_id?: string;
  source_id?: string;
  tracking_tag_data?: string;
  custom_tag_id?: string;
  custom_tag_value?: string;
  variation_id?: string;
  // Funnel view only: journeys follow the Test Variation toggle the row was counted with.
  test_variation_enabled?: number;
};

type AggregateCustomerJourneyProps = {
  opened: boolean;
  onClose: () => void;
  params: AggregateCustomerJourneyParams | null;
  entityLabel?: string;
};

type ConversionRow = {
  date: string;
  sales_amount: string;
  subscription_amount: string;
  email: string;
  visitor_session: string;
};

type DetailRow = {
  date: string;
  page_details: { page_url?: string; page_title?: string; postback_title?: string; webhook_title?: string };
  customer_email: string;
  traffic_source: string;
  sales_amount: string;
  attribution: string;
  behavioral_details: {
    ads_network_type?: string;
    network_tag?: Record<string, string>;
    custom_tag?: Record<string, string>;
    source_tag?: Record<string, string>;
  };
  device?: string;
  referral_source: string;
  v_no: string | number;
};

const PER_PAGE = 10;

export function AggregateCustomerJourney({
  opened,
  onClose,
  params,
  entityLabel,
}: AggregateCustomerJourneyProps) {
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const isDark = theme.colorScheme === 'dark';

  // List view state.
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<ConversionRow[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [goalLabel, setGoalLabel] = useState('');
  const [emailInput, setEmailInput] = useState('');
  /* The list is paged in SQL, so the date sort is resolved by the API - sorting the rows
     already on screen would only reorder the current page. */
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const requestSeq = useRef(0);

  // Detail view state.
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRows, setDetailRows] = useState<DetailRow[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchList = (pageNo: number, emailFilter: string, sortDirection: 'asc' | 'desc' = sortDir) => {
    if (!params) { return; }
    const seq = ++requestSeq.current;
    setLoading(true);
    AggregatedReportsAPI
      .getcustomerjourneylist({
        ...params,
        page: pageNo,
        number: PER_PAGE,
        sort_dir: sortDirection,
        customer_email: emailFilter,
        time_value: params.report_time,
      })
      .then((response: any) => {
        if (response.ok) { return response.json(); }
        if (response.status === 401 || response.status === 400) {
          navigate('/logout');
        }
        return null;
      })
      .then((data: any) => {
        if (seq !== requestSeq.current) { return; }
        if (data && data.status === 1 && data.data) {
          const list = Array.isArray(data.data.customer_journey_data) ? data.data.customer_journey_data : [];
          setRows(list);
          setTotalPages(typeof data.data.total_pages === 'number' && data.data.total_pages > 0 ? data.data.total_pages : 1);
          setTotalRecords(data.data.total_records ?? 0);
          setGoalLabel(typeof data.data.campaign_name === 'string' ? data.data.campaign_name : '');
        } else {
          setRows([]);
          setTotalPages(1);
          setTotalRecords(0);
        }
      })
      .catch(() => {
        if (seq !== requestSeq.current) { return; }
        setRows([]);
        setTotalPages(1);
        setTotalRecords(0);
      })
      .finally(() => {
        if (seq !== requestSeq.current) { return; }
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!opened) { return; }
    setPage(1);
    setEmailInput('');
    setSortDir('desc');
    setRows([]);
    setTotalPages(1);
    setTotalRecords(0);
    setDetailOpen(false);
    setDetailRows([]);
    fetchList(1, '', 'desc');
  }, [opened, params]);

  const handlePageChange = (next: number) => {
    setPage(next);
    fetchList(next, emailInput);
  };

  /* Sorting restarts at page 1 and keeps whatever email filter is currently applied. */
  const handleDateSort = () => {
    const nextSortDir: 'asc' | 'desc' = sortDir === 'desc' ? 'asc' : 'desc';
    setSortDir(nextSortDir);
    setPage(1);
    fetchList(1, emailInput, nextSortDir);
  };

  const handleEmailFilterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchList(1, emailInput);
  };

  const openDetail = (visitor_session: string) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailRows([]);
    CampaignReport.getcustomerjourneyviewdetail({ visitor_session_id: visitor_session })
      .then((response: any) => {
        if (response.ok) { return response.json(); }
        if (response.status === 401 || response.status === 400) {
          navigate('/logout');
        }
        return null;
      })
      .then((data: any) => {
        if (data && data.status === 1 && data.data && Array.isArray(data.data.journey_data)) {
          setDetailRows(data.data.journey_data as DetailRow[]);
        } else {
          setDetailRows([]);
        }
      })
      .catch(() => setDetailRows([]))
      .finally(() => setDetailLoading(false));
  };

  const surfaceBg     = isDark ? theme.colors.dark[6] : theme.colors.gray[0];
  const rowBorder     = isDark ? theme.colors.dark[5] : theme.colors.gray[2];
  const headerLabelCo = isDark ? theme.colors.dark[1] : theme.colors.gray[6];
  const numericCo     = isDark ? theme.colors.gray[3] : theme.colors.gray[8];
  const dimCo         = isDark ? theme.colors.dark[2] : theme.colors.gray[6];

  // Empty state — single shared component for both views.
  const EmptyState = ({ message }: { message: string }) => (
    <Flex direction="column" align="center" justify="center" py={48} sx={{ color: dimCo }}>
      <IconUsers size="2rem" stroke={1.4} />
      <Text size="sm" mt={8} color="dimmed">{message}</Text>
    </Flex>
  );

  const renderListRows = () => (
    rows.map((row, i) => (
      <tr key={i} style={{ borderTop: `1px solid ${rowBorder}` }}>
        <td style={{ padding: '10px 12px', width: '250px' }}>
          <Text size="sm">{row.date}</Text>
        </td>
        <td style={{ padding: '10px 12px', width: '250px' }}>
          <Text size="sm" weight={500} sx={{ color: numericCo, fontVariantNumeric: 'tabular-nums' }}>
            {row.sales_amount}
          </Text>
        </td>
        <td style={{ padding: '10px 12px', width: '250px' }}>
          <Text size="sm" sx={{ color: numericCo, fontVariantNumeric: 'tabular-nums' }}>
            {row.subscription_amount}
          </Text>
        </td>
        <td style={{ padding: '10px 12px', maxWidth: 220, overflowWrap: 'anywhere', wordBreak: 'break-all' }}>
          <Text size="sm" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-all' }}>{row.email}</Text>
        </td>
        <td style={{ padding: '10px 12px', textAlign: 'right', width: 90 }}>
          <UnstyledButton
            onClick={() => openDetail(row.visitor_session)}
            sx={(t) => ({
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '4px 10px',
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              color: t.colorScheme === 'dark' ? t.colors.teal[3] : t.colors.teal[7],
              backgroundColor: t.colorScheme === 'dark' ? t.fn.rgba(t.colors.teal[6], 0.15) : t.colors.teal[0],
              border: `1px solid ${t.colorScheme === 'dark' ? t.colors.teal[7] : t.colors.teal[2]}`,
              '&:hover': {
                backgroundColor: t.colorScheme === 'dark' ? t.fn.rgba(t.colors.teal[6], 0.25) : t.colors.teal[1],
              },
            })}
          >
            View
            <IconChevronRight size="0.75rem" />
          </UnstyledButton>
        </td>
      </tr>
    ))
  );

  /* The ad whose Assisted figure was clicked. Row ids arrive base64 encoded (the report
     encodes them in format_network_row), while the journey's network tags carry the raw
     platform id, so it is decoded once here for comparison. */
  const viewedAdId = useMemo(() => {
    const raw = params?.ad_entity_id || '';
    if (!raw) { return ''; }
    try { return atob(raw); } catch { return raw; }
  }, [params]);
  const isAssistedView = params?.journey_metric === 'assisted';

  const renderDetailRows = () => (
    detailRows.map((item, i) => {
      const networkTag = item.behavioral_details.network_tag || {};
      const customTag  = item.behavioral_details.custom_tag  || {};
      const sourceTag  = item.behavioral_details.source_tag  || {};
      const hasBehavioral = !!item.behavioral_details.ads_network_type
        || Object.keys(networkTag).length > 0
        || Object.keys(customTag).length > 0
        || Object.keys(sourceTag).length > 0;
      /* Which part this touchpoint played. Only meaningful on the Assisted drill-down, where
         the question is "why is this ad counted?" - elsewhere the rows stay unadorned. */
      const role: TouchpointRole = isAssistedView
        ? classifyTouchpoint({
            adValues: Object.values(networkTag).map((v) => String(v)),
            attribution: item.attribution || '',
            viewedAdId,
            attributionModel: params?.attribution_model || '',
          })
        : 'other';
      const accent = role === 'assisting' || role === 'both'
        ? theme.colors.violet[6]
        : (role === 'credited' ? theme.colors.teal[6] : 'transparent');
      return (
        <tr
          key={i}
          style={{
            borderTop: `1px solid ${rowBorder}`,
            verticalAlign: 'top',
            /* A left accent rather than a filled row: the journey is dense, and tinting whole
               rows would fight the badges and the benchmark colours already in the table. */
            boxShadow: role === 'other' ? undefined : `inset 3px 0 0 0 ${accent}`,
          }}
        >
          <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
            <Text size="xs" weight={500}>{item.date}</Text>
          </td>
          <td style={{ padding: '10px 12px', maxWidth: 220, overflowWrap: 'anywhere', wordBreak: 'break-all' }}>
            <Stack spacing={2}>
              {item.page_details.page_url ? (
                <Text size="xs" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-all' }}>{item.page_details.page_url}</Text>
              ) : null}
              {item.page_details.page_title ? (
                <Text size="xs" color="dimmed">{item.page_details.page_title}</Text>
              ) : null}
              {item.page_details.postback_title ? (
                <Badge
                  size="xs"
                  variant="outline"
                  color="grape"
                  sx={{
                    height: 'auto',
                    whiteSpace: 'normal',
                    textOverflow: 'clip',
                    padding: '2px 8px',
                    '& .mantine-Badge-inner': {
                      whiteSpace: 'normal',
                      overflow: 'visible',
                      textOverflow: 'clip',
                      lineHeight: 1.3,
                    },
                  }}
                >{`Postback: ${item.page_details.postback_title}`}</Badge>
              ) : null}
              {item.page_details.webhook_title ? (
                <Badge
                  size="md"
                  variant="outline"
                  color="indigo"
                  sx={{
                    height: 'auto',
                    whiteSpace: 'normal',
                    textOverflow: 'clip',
                    padding: '3px 10px',
                    '& .mantine-Badge-inner': {
                      whiteSpace: 'normal',
                      overflow: 'visible',
                      textOverflow: 'clip',
                      lineHeight: 1.3,
                    },
                  }}
                >{`Webhook: ${item.page_details.webhook_title}`}</Badge>
              ) : null}
            </Stack>
          </td>
          <td style={{ padding: '10px 12px', maxWidth: 180, overflowWrap: 'anywhere', wordBreak: 'break-all' }}>
            <Text size="xs" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-all' }}>{item.customer_email || '-'}</Text>
          </td>
          <td style={{ padding: '10px 12px' }}>
            <Text size="xs">{item.traffic_source || '-'}</Text>
          </td>
          <td style={{ padding: '10px 12px', textAlign: 'right' }}>
            <Text size="xs" sx={{ fontVariantNumeric: 'tabular-nums' }}>{item.sales_amount || '-'}</Text>
          </td>
          <td style={{ padding: '10px 12px' }}>
            <Stack spacing={4}>
              {item.attribution ? (
                <Badge size="sm" variant="light" color="teal">{item.attribution}</Badge>
              ) : null}
              {/* Says which of the two ads this row is, so the Assisted count explains itself. */}
              {role === 'assisting' ? (
                <Badge size="sm" variant="filled" color="violet">Assisting ad</Badge>
              ) : null}
              {role === 'credited' ? (
                <Badge size="sm" variant="outline" color="teal">Credited ad</Badge>
              ) : null}
              {role === 'both' ? (
                <Badge size="sm" variant="filled" color="violet">This ad · credited here</Badge>
              ) : null}
            </Stack>
          </td>
          <td style={{ padding: '10px 12px', maxWidth: 260, overflowWrap: 'anywhere', wordBreak: 'break-all' }}>
            {hasBehavioral ? (
              <Stack spacing={2}>
                {item.behavioral_details.ads_network_type ? (
                  <Text size="xs" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-all' }}>
                    <Text component="span" color="dimmed">Network · </Text>{item.behavioral_details.ads_network_type}
                  </Text>
                ) : null}
                {Object.entries(networkTag).map(([k, v]) => (
                  <Text size="xs" key={'n_' + k} sx={{ overflowWrap: 'anywhere', wordBreak: 'break-all' }}>
                    <Text component="span" color="dimmed">{k} · </Text>{v}
                  </Text>
                ))}
                {Object.entries(customTag).map(([k, v]) => (
                  <Text size="xs" key={'c_' + k} sx={{ overflowWrap: 'anywhere', wordBreak: 'break-all' }}>
                    <Text component="span" color="dimmed">{k} · </Text>{v}
                  </Text>
                ))}
                {Object.entries(sourceTag).map(([k, v]) => (
                  <Text size="xs" key={'s_' + k} sx={{ overflowWrap: 'anywhere', wordBreak: 'break-all' }}>
                    <Text component="span" color="dimmed">{k} · </Text>{v}
                  </Text>
                ))}
              </Stack>
            ) : (
              <Text size="xs" color="dimmed">-</Text>
            )}
          </td>
          <td style={{ padding: '10px 12px' }}>
            <Text size="xs">{item.device || '-'}</Text>
          </td>
          <td style={{ padding: '10px 12px', maxWidth: 160, overflowWrap: 'anywhere', wordBreak: 'break-all' }}>
            <Text size="xs" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-all' }}>{item.referral_source || '-'}</Text>
          </td>
          <td style={{ padding: '10px 12px', maxWidth: 160, overflowWrap: 'anywhere', wordBreak: 'break-all' }}>
            <Text size="xs" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-all' }}>{item.v_no || '-'}</Text>
          </td>
        </tr>
      );
    })
  );

  const headerSubtitle = entityLabel || '';

  return (
    <>
      {/* List modal */}
      <Modal.Root opened={opened} onClose={onClose} size="80%" trapFocus={false}>
        <Modal.Overlay />
        <Modal.Content>
          <VisiLoader VisiLoaderVisible={loading} />
          <Modal.Header sx={{ paddingBottom: 12 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Group spacing={8} mb={2} noWrap>
                <Text size="lg" weight={700} sx={{ lineHeight: 1.2 }}>Customer Journey</Text>
                {/* Says plainly that these are assists, not conversions credited to this ad. */}
                {params?.journey_metric === 'assisted' ? (
                  <Badge size="md" variant="light" color="indigo" sx={{ textTransform: 'none', fontWeight: 600 }}>
                    Assisted Conversion
                  </Badge>
                ) : null}
                {goalLabel ? (
                  <Badge size="md" variant="light" color="teal" sx={{ textTransform: 'none', fontWeight: 600 }}>
                    {goalLabel}
                  </Badge>
                ) : null}
              </Group>
              {headerSubtitle ? (
                <Text size="sm" color="dimmed" sx={{ wordBreak: 'break-word' }}>{headerSubtitle}</Text>
              ) : null}
            </Box>
            <Modal.CloseButton />
          </Modal.Header>

          <Modal.Body sx={{ paddingTop: 0 }} h={520}>
            <Paper
              withBorder
              radius="md"
              p="sm"
              mb="sm"
              sx={{ backgroundColor: surfaceBg, borderColor: rowBorder }}
            >
              <form onSubmit={handleEmailFilterSubmit}>
                <Flex align="center" gap="sm" wrap="wrap">
                  <TextInput
                    size="sm"
                    placeholder="Search by email"
                    icon={<IconMail size="0.9rem" />}
                    value={emailInput}
                    onChange={(e: any) => setEmailInput(e.target.value)}
                    sx={{ flex: 1, minWidth: 220 }}
                  />
                  <Button type="submit" size="sm" variant="filled" color="teal">
                    Filter
                  </Button>
                  <Box sx={{ marginLeft: 'auto' }}>
                    <Text size="xs" color="dimmed" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {totalRecords > 0
                        ? `${totalRecords.toLocaleString()} customer${totalRecords === 1 ? '' : 's'}`
                        : (loading ? 'Loading…' : '0 customers')}
                    </Text>
                  </Box>
                </Flex>
              </form>
            </Paper>

            <Paper withBorder radius="md" sx={{ backgroundColor: 'transparent', borderColor: rowBorder, overflow: 'hidden' }}>
              <SimpleBar autoHide={false} style={{ maxHeight: 380 }}>
                {!loading && rows.length === 0 ? (
                  <EmptyState message="No conversions match the current filters." />
                ) : (
                  <Table
                    withColumnBorders
                    sx={{
                      marginBottom: 0,
                      'th, td': { borderColor: `${rowBorder} !important` },
                    }}
                  >
                    <thead style={{ position: 'sticky', top: 0, backgroundColor: surfaceBg, zIndex: 1 }}>
                      <tr>
                        <th
                          style={{ padding: '10px 12px', width: '250px', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                          onClick={handleDateSort}
                          title={sortDir === 'desc' ? 'Sorted newest first - click for oldest first' : 'Sorted oldest first - click for newest first'}
                        >
                          <Text size="xs" transform="uppercase" weight={700} sx={{ color: headerLabelCo, letterSpacing: 0.4 }}>
                            {'Date '}
                            {sortDir === 'desc'
                              ? <IconChevronDown size="0.8rem" style={{ marginBottom: -2 }} />
                              : <IconChevronUp size="0.8rem" style={{ marginBottom: -2 }} />}
                          </Text>
                        </th>
                        <th style={{ padding: '10px 12px', width: '250px' }}>
                          <Text size="xs" transform="uppercase" weight={700} sx={{ color: headerLabelCo, letterSpacing: 0.4 }}>Sales</Text>
                        </th>
                        <th style={{ padding: '10px 12px', width: '250px' }}>
                          <Text size="xs" transform="uppercase" weight={700} sx={{ color: headerLabelCo, letterSpacing: 0.4 }}>Subscription</Text>
                        </th>
                        <th style={{ padding: '10px 12px', textAlign: 'left' }}>
                          <Text size="xs" transform="uppercase" weight={700} sx={{ color: headerLabelCo, letterSpacing: 0.4 }}>Email</Text>
                        </th>
                        <th style={{ padding: '10px 12px', textAlign: 'right' }}></th>
                      </tr>
                    </thead>
                    <tbody>{renderListRows()}</tbody>
                  </Table>
                )}
              </SimpleBar>
            </Paper>

            {totalPages > 1 && (
              <Flex justify="center" mt="md">
                <Pagination.Root total={totalPages} value={page} onChange={handlePageChange}>
                  <Group spacing={5}>
                    <Pagination.First />
                    <Pagination.Previous />
                    <Pagination.Items />
                    <Pagination.Next />
                    <Pagination.Last />
                  </Group>
                </Pagination.Root>
              </Flex>
            )}
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>

      {/* Detail (per-session) modal */}
      <Modal.Root opened={detailOpen} onClose={() => setDetailOpen(false)} size="90%" trapFocus={false}>
        <Modal.Overlay />
        <Modal.Content>
          <VisiLoader VisiLoaderVisible={detailLoading} />
          <Modal.Header sx={{ paddingBottom: 12 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Group spacing={8} mb={2} noWrap>
                <ActionIcon variant="subtle" size="md" onClick={() => setDetailOpen(false)} title="Back">
                  <IconArrowLeft size="1.1rem" />
                </ActionIcon>
                <Text size="lg" weight={700} sx={{ lineHeight: 1.2 }}>Customer Journey Report</Text>
                {goalLabel ? (
                  <Badge size="md" variant="light" color="teal" sx={{ textTransform: 'none', fontWeight: 600 }}>
                    {goalLabel}
                  </Badge>
                ) : null}
              </Group>
              {headerSubtitle ? (
                <Text size="xs" color="dimmed" sx={{ wordBreak: 'break-word' }}>{headerSubtitle}</Text>
              ) : null}
            </Box>
            <Group spacing="xs" noWrap>
              <Button
                size="xs"
                leftIcon={<IconArrowLeft size="0.9rem" />}
                onClick={() => setDetailOpen(false)}
              >
                Back
              </Button>
              <Modal.CloseButton />
            </Group>
          </Modal.Header>

          <Modal.Body sx={{ paddingTop: 0 }} h={520}>
            {/* Why this journey is on the Assisted list. Stated once, above the table, rather
                than repeated on every row - the badges below then just point at the two ads. */}
            {isAssistedView ? (
              <Paper
                withBorder
                radius="md"
                p="xs"
                mb="xs"
                sx={{ borderColor: rowBorder, background: 'transparent' }}
              >
                <Group spacing="xs" noWrap align="flex-start">
                  <Badge size="sm" variant="filled" color="violet" sx={{ flexShrink: 0 }}>Assisting ad</Badge>
                  <Text size="xs" color="dimmed">
                    was part of this journey but {attributionLabelOf(params?.attribution_model || '') || 'the attribution model'} credited the
                  </Text>
                  <Badge size="sm" variant="outline" color="teal" sx={{ flexShrink: 0 }}>Credited ad</Badge>
                  <Text size="xs" color="dimmed">
                    — so the conversion counts as an Assisted Conversion, and its full value counts as Influenced Revenue.
                  </Text>
                </Group>
              </Paper>
            ) : null}
            <Paper withBorder radius="md" sx={{ borderColor: rowBorder, overflow: 'hidden' }}>
              <SimpleBar autoHide={false} style={{ maxHeight: 460 }}>
                {!detailLoading && detailRows.length === 0 ? (
                  <EmptyState message="No journey data found for this session." />
                ) : (
                  <Table
                    withColumnBorders
                    sx={{
                      marginBottom: 0,
                      'th, td': { borderColor: `${rowBorder} !important` },
                    }}
                  >
                    <thead style={{ position: 'sticky', top: 0, backgroundColor: surfaceBg, zIndex: 1 }}>
                      <tr>
                        <th style={{ padding: '10px 12px', textAlign: 'left' }}>
                          <Text size="xs" transform="uppercase" weight={600} sx={{ color: headerLabelCo }}>Date</Text>
                        </th>
                        <th style={{ padding: '10px 12px', textAlign: 'left' }}>
                          <Text size="xs" transform="uppercase" weight={600} sx={{ color: headerLabelCo }}>Page Details</Text>
                        </th>
                        <th style={{ padding: '10px 12px', textAlign: 'left' }}>
                          <Text size="xs" transform="uppercase" weight={600} sx={{ color: headerLabelCo }}>Customer Email</Text>
                        </th>
                        <th style={{ padding: '10px 12px', textAlign: 'left' }}>
                          <Text size="xs" transform="uppercase" weight={600} sx={{ color: headerLabelCo }}>Traffic Source</Text>
                        </th>
                        <th style={{ padding: '10px 12px', textAlign: 'right' }}>
                          <Text size="xs" transform="uppercase" weight={600} sx={{ color: headerLabelCo }}>Sales Amount</Text>
                        </th>
                        <th style={{ padding: '10px 12px', textAlign: 'left' }}>
                          <Text size="xs" transform="uppercase" weight={600} sx={{ color: headerLabelCo }}>Attribution</Text>
                        </th>
                        <th style={{ padding: '10px 12px', textAlign: 'left' }}>
                          <Text size="xs" transform="uppercase" weight={600} sx={{ color: headerLabelCo }}>Behavioral Details</Text>
                        </th>
                        <th style={{ padding: '10px 12px', textAlign: 'left' }}>
                          <Text size="xs" transform="uppercase" weight={600} sx={{ color: headerLabelCo }}>Device</Text>
                        </th>
                        <th style={{ padding: '10px 12px', textAlign: 'left' }}>
                          <Text size="xs" transform="uppercase" weight={600} sx={{ color: headerLabelCo }}>Referral Source</Text>
                        </th>
                        <th style={{ padding: '10px 12px', textAlign: 'left' }}>
                          <Text size="xs" transform="uppercase" weight={600} sx={{ color: headerLabelCo }}>V.No.</Text>
                        </th>
                      </tr>
                    </thead>
                    <tbody>{renderDetailRows()}</tbody>
                  </Table>
                )}
              </SimpleBar>
            </Paper>
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>
    </>
  );
}
